import { ActionIcon, Avatar, Group, Text, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconRefresh, IconUser } from '@tabler/icons-react';
import { WindowBadge } from './WindowBadge';
import type { ChannelCapabilities, InboxHeader, InboxWindowState } from './types';

/** Thread header: avatar + name + identifier + window indicator + per-channel
 *  actions (profile refresh, open-in-CRM), all capability-gated. */
export function ChatHeader({
  header,
  windowState,
  capabilities,
  onRefreshProfile,
  refreshing,
}: {
  header: InboxHeader;
  windowState: InboxWindowState;
  capabilities: ChannelCapabilities;
  onRefreshProfile?: () => void;
  refreshing?: boolean;
}) {
  const Icon = capabilities.channelIcon;
  return (
    <Group p="sm" justify="space-between" wrap="nowrap">
      <Group wrap="nowrap" style={{ minWidth: 0 }}>
        <Avatar size="md" radius="xl" src={header.avatarUrl ?? undefined}>
          <Icon size={14} />
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <Text fw={600} truncate>
            {header.title}
          </Text>
          {header.typingLabel ? (
            <Text size="xs" c="teal" truncate>
              {header.typingLabel}
            </Text>
          ) : (
            header.subtitle && (
              <Text c="dimmed" size="xs" ff="monospace" truncate>
                {header.subtitle}
              </Text>
            )
          )}
        </div>
      </Group>
      <Group gap="xs" wrap="nowrap">
        <WindowBadge state={windowState} />
        {capabilities.header.showOpenContactLink && header.contactUid && (
          <Tooltip label="Open contact in CRM">
            <ActionIcon
              variant="subtle"
              color="gray"
              renderRoot={(props) => (
                <Link to="/vendor/contacts/$uid" params={{ uid: header.contactUid! }} {...props} />
              )}
            >
              <IconUser size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        {capabilities.header.showProfileRefresh && onRefreshProfile && (
          <Tooltip label="Refresh profile">
            <ActionIcon
              variant="subtle"
              color="gray"
              loading={refreshing}
              onClick={onRefreshProfile}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Group>
  );
}
